import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { useAuth } from 'src/contexts/auth'
import { useUpdateSale } from 'src/hooks/api/sale'
import { SaleDTO, UpdateSaleDTO } from 'src/dto/sale'
import { useAnimalSaleOperations } from './useAnimalSaleOperations'

interface UseSaleDeleteProps {
  saleData: SaleDTO | undefined | null
  onClose: () => void
}

export const useSaleDelete = ({ saleData, onClose }: UseSaleDeleteProps) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  const { revertAnimalsOnSaleDelete } = useAnimalSaleOperations()
  const { mutateAsync: updateSaleAction } = useUpdateSale()

  const { userFarm: farmId } = useAuth()
  const queryClient = useQueryClient()

  const handleDeleteSale = useCallback(() => {
    if (!saleData?.idVenda) {
      toast.error('Venda não encontrada')
      return
    }
    setShowDeleteConfirmation(true)
  }, [saleData])

  const confirmDeleteSale = useCallback(async () => {
    if (!saleData?.idVenda) return
    setIsDeleting(true)
    try {
      await updateSaleAction({
        ...saleData,
        idVenda: saleData.idVenda,
        acao: 'Removido'
      } as UpdateSaleDTO)

      if (saleData.animais && saleData.animais.length > 0) {
        await revertAnimalsOnSaleDelete(saleData.animais)
      }

      queryClient.invalidateQueries({ queryKey: ['getSaleList', farmId!] })

      toastSyncSuccess('Venda excluída com sucesso!')
      onClose()
    } catch (err: any) {
      console.error('Erro ao excluir venda:', err)
      toast.error('Erro ao excluir venda, tente novamente.')
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirmation(false)
    }
  }, [saleData, farmId, updateSaleAction, revertAnimalsOnSaleDelete, queryClient, onClose])

  return {
    isDeleting,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    handleDeleteSale,
    confirmDeleteSale
  }
}
